import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Card, CardContent, Avatar,
  CircularProgress, Alert
} from '@mui/material';
import { Visibility, Check, Close, Search, Refresh } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api';
const IMG_URL = 'https://api.bharatdarshan.app/';

const ProductApprovals = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', product: null });
  const [rejectReason, setRejectReason] = useState('');
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [categories, setCategories] = useState([]);

  const rejectReasons = ['Poor Image Quality', 'Inappropriate Content', 'Incomplete Description', 'Wrong Category', 'Price Issues', 'Duplicate Product', 'Trademark Violation', 'Other'];

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('page', page + 1);
      params.append('limit', rowsPerPage);
      if (statusFilter !== 'all') params.append('approvalStatus', statusFilter);

      const res = await axios.get(`${API_BASE}/marketplace/admin/products?${params.toString()}`);
      if (res.data && res.data.success) {
        const list = res.data.products || [];
        setProducts(list);
        setTotalProducts((res.data.pagination && res.data.pagination.total) || list.length);

        // Build category list from loaded products (vendor products only)
        const catSet = new Set();
        list.forEach(p => {
          const cn = p.categoryId && p.categoryId.categoryName;
          if (cn) catSet.add(cn.trim());
        });
        if (catSet.size > 0) setCategories(prev => Array.from(new Set([...prev, ...Array.from(catSet)])).sort());
      } else {
        setError((res.data && res.data.message) || 'Failed to fetch products');
      }
    } catch (err) {
      console.error('Fetch products error:', err);
      setError((err.response && err.response.data && err.response.data.message) || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const dash = await axios.get(`${API_BASE}/marketplace/admin/dashboard`);
      const s = (dash.data && dash.data.stats && dash.data.stats.products) || {};
      setStats({ total: s.total || 0, pending: s.pending || 0, approved: s.live || 0, rejected: 0 });
    } catch (err) {
      console.log('Stats fetch error:', err.message);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const getStatusColor = (status) => {
    switch (status) { case 'PENDING': return 'warning'; case 'APPROVED': return 'success'; case 'REJECTED': return 'error'; default: return 'default'; }
  };

  const handleApprove = (product) => setActionDialog({ open: true, type: 'approve', product });
  const handleReject = (product) => setActionDialog({ open: true, type: 'reject', product });

  const confirmAction = async () => {
    const { type, product } = actionDialog;
    if (!product) return;
    try {
      // Use vendor admin product approval endpoint
      const url = `${API_BASE}/vendor/admin/product/${product._id}`;
      const body = type === 'approve'
        ? { action: 'approve' }
        : { action: 'reject', reason: rejectReason };
      const res = await axios.put(url, body);
      if (res.data && (res.data.success || res.status === 200)) {
        setActionDialog({ open: false, type: '', product: null });
        setRejectReason('');
        fetchProducts();
        fetchStats();
      } else {
        alert((res.data && res.data.message) || 'Action failed');
      }
    } catch (err) {
      console.error('Approval error:', err);
      alert((err.response && err.response.data && err.response.data.message) || 'Action failed');
    }
  };

  const filteredProducts = products.filter(p => {
    const q = searchQuery.toLowerCase();
    const name = (p.productName || '').toLowerCase();
    const sku = (p.sku || '').toLowerCase();
    const vendorName = ((p.vendorId && p.vendorId.businessName) || '').toLowerCase();
    const matchesSearch = !q || name.includes(q) || vendorName.includes(q) || sku.includes(q);
    const matchesCategory = categoryFilter === 'all' || ((p.categoryId && p.categoryId.categoryName) || '').trim() === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

  const getVendorName = (p) => {
    if (p.vendorId && typeof p.vendorId === 'object') return p.vendorId.businessName || p.vendorId.ownerName || 'Vendor';
    return p.sellerType === 'PLATFORM' ? 'Platform' : 'Vendor';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>Product Approvals</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => { fetchProducts(); fetchStats(); }}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'Total Products', value: stats.total, color: '#2196F3' },
          { label: 'Pending Approval', value: stats.pending, color: '#FF9800' },
          { label: 'Approved / Live', value: stats.approved, color: '#4CAF50' },
          { label: 'Rejected', value: stats.rejected, color: '#F44336' }
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ bgcolor: stat.color, color: 'white' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stat.value}</Typography>
                <Typography>{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" placeholder="Search products, vendor, SKU..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="textSecondary" textAlign="center">Showing {filteredProducts.length} of {totalProducts}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Table>
            <TableHead sx={{ bgcolor: '#4CAF50' }}>
              <TableRow>
                {['Product', 'Vendor', 'Category', 'Price', 'Stock', 'Status', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center"><Typography color="textSecondary" sx={{ py: 4 }}>No products found</Typography></TableCell></TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {(product.bannerImages && product.bannerImages[0]) || product.image ? (
                          <Avatar src={(()=>{const raw=(product.bannerImages && product.bannerImages[0]) || product.image || "";return raw && raw.toString().startsWith("http") ? raw : (raw ? IMG_URL + raw : undefined);})()} variant="rounded" sx={{ width: 40, height: 40, mr: 1 }} />
                        ) : null}
                        <Box>
                          <Typography fontWeight="bold">{product.productName}</Typography>
                          {product.sku && <Typography variant="caption" color="textSecondary">SKU: {product.sku}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{getVendorName(product)}</TableCell>
                    <TableCell><div style={{display:'flex',flexDirection:'column',gap:2}}><Chip label={(product.categoryId && product.categoryId.categoryName) || 'N/A'} size="small" />{product.subCategoryId && product.subCategoryId.subCategoryName && <Chip label={product.subCategoryId.subCategoryName} size="small" color="primary" variant="outlined" />}{product.childCategoryId && product.childCategoryId.childCategoryName && <Chip label={product.childCategoryId.childCategoryName} size="small" color="secondary" variant="outlined" />}</div></TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">{formatCurrency(product.price)}</Typography>
                      {product.mrp > product.price && <Typography variant="caption" sx={{ textDecoration: 'line-through' }}>{formatCurrency(product.mrp)}</Typography>}
                    </TableCell>
                    <TableCell>{product.quantity != null ? product.quantity : (product.inventory || 0)}</TableCell>
                    <TableCell>
                      <Chip label={product.approvalStatus || 'PENDING'} color={getStatusColor(product.approvalStatus || 'PENDING')} size="small" />
                      {product.isLive && <Chip label="LIVE" color="success" size="small" sx={{ ml: 0.5 }} variant="outlined" />}
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => { setSelectedProduct(product); setDetailDialog(true); }}><Visibility /></IconButton>
                      {(product.approvalStatus || 'PENDING') !== 'APPROVED' && (
                        <IconButton color="success" onClick={() => handleApprove(product)}><Check /></IconButton>
                      )}
                      {(product.approvalStatus || 'PENDING') !== 'REJECTED' && (
                        <IconButton color="error" onClick={() => handleReject(product)}><Close /></IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={totalProducts} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
      </TableContainer>

      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4CAF50', color: 'white' }}>Product Details</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedProduct && (
            <Grid container spacing={2}>
              <Grid item xs={12}><Typography variant="h6">{selectedProduct.productName}</Typography></Grid>
              {selectedProduct.sku && <Grid item xs={6}><Typography><b>SKU:</b> {selectedProduct.sku}</Typography></Grid>}
              <Grid item xs={6}><Typography><b>Category:</b> {(selectedProduct.categoryId && selectedProduct.categoryId.categoryName) || 'N/A'}</Typography></Grid>
              <Grid item xs={6}><Typography><b>Sub Category:</b> {(selectedProduct.subCategoryId && selectedProduct.subCategoryId.subCategoryName) || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography><b>Child Category:</b> {(selectedProduct.childCategoryId && selectedProduct.childCategoryId.childCategoryName) || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography><b>Vendor:</b> {getVendorName(selectedProduct)}</Typography></Grid>
              <Grid item xs={6}><Typography><b>Status:</b> <Chip label={selectedProduct.approvalStatus || 'PENDING'} color={getStatusColor(selectedProduct.approvalStatus || 'PENDING')} size="small" /></Typography></Grid>
              <Grid item xs={4}><Typography><b>Price:</b> {formatCurrency(selectedProduct.price)}</Typography></Grid>
              <Grid item xs={4}><Typography><b>MRP:</b> {formatCurrency(selectedProduct.mrp)}</Typography></Grid>
              <Grid item xs={4}><Typography><b>Stock:</b> {selectedProduct.quantity != null ? selectedProduct.quantity : (selectedProduct.inventory || 0)}</Typography></Grid>
              <Grid item xs={12}><Typography><b>Description:</b></Typography><Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>{selectedProduct.description || '—'}</Paper></Grid>
              <Grid item xs={6}><Typography><b>Images:</b> {(selectedProduct.bannerImages && selectedProduct.bannerImages.length) || 0} uploaded</Typography></Grid>
              <Grid item xs={6}><Typography><b>Created:</b> {selectedProduct.createdAt ? new Date(selectedProduct.createdAt).toLocaleDateString() : 'N/A'}</Typography></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
          {selectedProduct && (selectedProduct.approvalStatus || 'PENDING') !== 'APPROVED' && (
            <Button variant="contained" color="success" onClick={() => { setDetailDialog(false); handleApprove(selectedProduct); }}>Approve</Button>
          )}
          {selectedProduct && (selectedProduct.approvalStatus || 'PENDING') !== 'REJECTED' && (
            <Button variant="contained" color="error" onClick={() => { setDetailDialog(false); handleReject(selectedProduct); }}>Reject</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, type: '', product: null })}>
        <DialogTitle>{actionDialog.type === 'approve' ? 'Approve Product' : 'Reject Product'}</DialogTitle>
        <DialogContent>
          {actionDialog.type === 'approve' ? (
            <Typography>Approve <b>{actionDialog.product && actionDialog.product.productName}</b>?</Typography>
          ) : (
            <>
              <Typography sx={{ mb: 2 }}>Select rejection reason:</Typography>
              <FormControl fullWidth>
                <InputLabel>Rejection Reason</InputLabel>
                <Select value={rejectReason} label="Rejection Reason" onChange={(e) => setRejectReason(e.target.value)}>
                  {rejectReasons.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: '', product: null })}>Cancel</Button>
          <Button variant="contained" color={actionDialog.type === 'approve' ? 'success' : 'error'} onClick={confirmAction} disabled={actionDialog.type === 'reject' && !rejectReason}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductApprovals;
